/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// SafeCircle Live Activity — React binding over the native MotionActivity
// plugin. Mirrors the useNativePushToken pattern: platform-gate, permission
// flow, subscribe on mount / unsubscribe on unmount, expose a small typed
// surface. This hook ONLY reads motion + manages the subscription; it does
// NOT write to RTDB — that gating lives in ActivityContext so the privacy
// rules (opt-in, SafeCircle-only) stay in one place.

import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import {
  type ActivityChangeEvent,
  type ActivityPermissionStatus,
  MotionActivity,
} from "@/utils/motionActivity";
import type {
  ActivityConfidence,
  ActivityState,
} from "@/utils/activity/activityState";

export interface UseMotionActivityResult {
  state: ActivityState;
  confidence: ActivityConfidence;
  /** False on web and on devices without a motion coprocessor. */
  isAvailable: boolean;
  permissionStatus: ActivityPermissionStatus;
  /** Triggers the system motion prompt; resolves with the new status. */
  requestPermission: () => Promise<ActivityPermissionStatus>;
  /** Begin native activity updates (no-op off-device / when unavailable). */
  start: () => Promise<void>;
  /** Stop native activity updates. */
  stop: () => Promise<void>;
}

function isNativePlatform(): boolean {
  const p = Capacitor.getPlatform();
  return p === "ios" || p === "android";
}

export function useMotionActivity(): UseMotionActivityResult {
  const [state, setState] = useState<ActivityState>("unknown");
  const [confidence, setConfidence] = useState<ActivityConfidence>("low");
  const [isAvailable, setIsAvailable] = useState(false);
  const [permissionStatus, setPermissionStatus] =
    useState<ActivityPermissionStatus>("prompt");
  const listenerRef = useRef<PluginListenerHandle | null>(null);

  // Initial availability + permission probe. Web/unsupported resolves to
  // unavailable and the hook stays inert.
  useEffect(() => {
    if (!isNativePlatform()) {
      setIsAvailable(false);
      setPermissionStatus("unavailable");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { status } = await MotionActivity.checkPermissions();
        if (cancelled) return;
        setPermissionStatus(status);
        setIsAvailable(status !== "unavailable");
      } catch (err) {
        if (cancelled) return;
        console.warn("[useMotionActivity] checkPermissions failed:", err);
        setIsAvailable(false);
        setPermissionStatus("unknown");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Tear down any live listener on unmount.
  useEffect(() => {
    return () => {
      void listenerRef.current?.remove();
      listenerRef.current = null;
    };
  }, []);

  const requestPermission =
    useCallback(async (): Promise<ActivityPermissionStatus> => {
      if (!isNativePlatform()) return "unavailable";
      try {
        const { status } = await MotionActivity.requestPermissions();
        setPermissionStatus(status);
        setIsAvailable(status !== "unavailable");
        return status;
      } catch (err) {
        console.warn("[useMotionActivity] requestPermissions failed:", err);
        setPermissionStatus("unknown");
        return "unknown";
      }
    }, []);

  const start = useCallback(async (): Promise<void> => {
    if (!isNativePlatform()) return;
    try {
      if (!listenerRef.current) {
        listenerRef.current = await MotionActivity.addListener(
          "activityChange",
          (event: ActivityChangeEvent) => {
            setState(event.state);
            setConfidence(event.confidence);
          },
        );
      }
      await MotionActivity.start();
    } catch (err) {
      console.warn("[useMotionActivity] start failed:", err);
    }
  }, []);

  const stop = useCallback(async (): Promise<void> => {
    if (!isNativePlatform()) return;
    try {
      await MotionActivity.stop();
    } catch (err) {
      console.warn("[useMotionActivity] stop failed:", err);
    } finally {
      await listenerRef.current?.remove();
      listenerRef.current = null;
      setState("unknown");
    }
  }, []);

  return {
    state,
    confidence,
    isAvailable,
    permissionStatus,
    requestPermission,
    start,
    stop,
  };
}
